import { get, set, unset, isArray, isEqual, isObject, find, cloneDeep } from "lodash";
import PutRecord from "../../../lib/api/PutRecord";
import { PostRecord } from "../../../lib/api/PostRecord";
import { removeDataKeys, removeNullDeep } from "../../../utils/helper";
// import { logDev } from "../../../utils/logger";

interface JsonObject {
    [key: string]: any;
}

const isElemInArray = (data: JsonObject, path: string) => {
    const pathWithouEndingIndices = path.replace(/\[\d+\]$/, '');
    const array = get(data, pathWithouEndingIndices);
    if (isArray(array)) {
        return true;
    } else {
        return false;
    }
};

const deleteElemInArray = (data: JsonObject, path: string) => {
    const pathWithouEndingIndices = path.replace(/\[\d+\]$/, '');
    //extract the index or return the path if no index found at end of string
    let index: string | undefined = path.replace(/^.*\[(\d+)\]$/, '$1');
    // if path was returned, set undefined
    index = path === index ? undefined : index;
    const indexNumber = Number(index);
    const array = cloneDeep(get(data, pathWithouEndingIndices, []));

    if (isArray(array) && !isNaN(indexNumber)) {
        // delete element indexNumber in array
        array.splice(indexNumber, 1);
        if (array.length === 0) {
            unset(data, pathWithouEndingIndices);
        } else {
            set(data, pathWithouEndingIndices, array);
        }
    }
};

const getAttributeApiName = (
    schema: any,
    attributePath: string
): string | undefined => {
    if (attributePath.length === 0) {
        return undefined;
    }
    let currentSchema = cloneDeep(schema);
    let attributeAPI: string | undefined;
    const attrPathNoIndices = attributePath.replace(/\[\d+\]/g, '');
    const attrPathArray = attrPathNoIndices.split('.');
    const filteredAttrPath = attrPathArray.filter(
        (attr) => attr !== 'attributes'
    );
    // remove 'data' path
    if (filteredAttrPath.length !== 0 && filteredAttrPath[0] === 'data') {
        filteredAttrPath.shift();
    }

    // Traverse the schema using the path
    for (const part of filteredAttrPath) {
        const attribute = get(currentSchema, 'attributes.' + part);
        if (attribute) {
            attributeAPI = get(attribute, 'target');
            if (attributeAPI && attributeAPI.length !== 0) {
                // get "apinames" from "api::apiname.apiname"
                attributeAPI = attributeAPI.split('::')[1].split('.')[0] + 's';
            }

            const tmpSchema = get(attribute, 'schema');
            // If the attribute has a nested schema, update currentSchema
            if (tmpSchema) {
                currentSchema = tmpSchema;
            }
        } else {
            // Attribute not found in schema
            return undefined;
        }
    }
    return attributeAPI;
};

const getAttributeType = (
    schema: any,
    attributePath: string
): string | undefined => {
    if (attributePath.length === 0) {
        return undefined;
    }

    let currentSchema = cloneDeep(schema);
    let attributeType: string | undefined;
    const attrPathNoIndices = attributePath.replace(/\[\d+\]/g, '');
    const attrPathArray = attrPathNoIndices.split('.');
    const filteredAttrPath = attrPathArray.filter(
        (attr) => attr !== 'attributes'
    );
    // remove 'data' path
    if (filteredAttrPath.length !== 0 && filteredAttrPath[0] === 'data') {
        filteredAttrPath.shift();
    }

    // Traverse the schema using the path
    for (const part of filteredAttrPath) {
        const attribute = get(currentSchema, 'attributes.' + part);
        if (attribute) {
            attributeType = get(attribute, 'type');
            const tmpSchema = get(attribute, 'schema');
            // If the attribute has a nested schema, update currentSchema
            if (tmpSchema) {
                currentSchema = tmpSchema;
            }
        } else {
            // Attribute not found in schema
            return undefined;
        }
    }

    return attributeType;
};

const getParentAttributeType = (
    schema: any,
    path: string
): string | undefined => {
    const parentPath = path.slice(0, path.lastIndexOf('.'));
    if (parentPath === path) {
        return undefined;
    }
    const parentType = getAttributeType(schema, parentPath);
    return parentType;
};

const handleDiffArray = (app: JsonObject, updates: JsonObject, path: string, appPathValue: any, updPathValue: any, attrType: string | undefined, pathStack: string[], differences: JsonObject
) => {
    const updPathValueCopy = cloneDeep(updPathValue);
    // relation -> push element to pathStack if updPathValue has no id or if updPathValue and appPathValue elements are different
    if (attrType === 'relation') {
        // array to set in obj1, obj2 to compare later
        const arrayOrigin: JsonObject[] = [];
        const arrayUpdates: JsonObject[] = [];
        // iterate over updPathValue array and check Id
        for (let i = 0; i < updPathValueCopy.length; i++) {
            if (updPathValueCopy[i].id) {
                // find element of appPathValue with the same id as updPathValue element i
                const originItem = find(appPathValue, { id: updPathValueCopy[i].id });
                // if no element found or if found element is different to updPathValue element
                if (!isEqual(originItem, updPathValueCopy[i])) {
                    arrayOrigin.push(originItem);
                    arrayUpdates.push(updPathValueCopy[i]);
                }
            } else {
                arrayOrigin.push({});
                arrayUpdates.push(updPathValueCopy[i]);
            }
        }

        // push new elems to path stack
        for (let i = 0; i < arrayUpdates.length; i++) {
            const lastIndex = arrayUpdates.length - 1;
            const newPath = path + `[${lastIndex - i}]`;
            // logDev("newPath: ", path, lastIndex, i, arrayUpdates.length);
            pathStack.push(newPath);
        }
        // logDev('relation: ', path, updPathValue);
        set(app, path, arrayOrigin);
        set(updates, path, arrayUpdates);

        // component ->
    } else if (attrType === 'component') {
        set(differences, path, updPathValue);
        for (let i = 0; i < updPathValueCopy.length; i++) {
            const attrKeys = Object.keys(updPathValueCopy[i]);
            for (const key of attrKeys) {
                const newPath = path + `[${i}].` + key;
                pathStack.push(newPath);
            }
        }
    } else if (attrType === 'media') {
        for (let i = 0; i < updPathValueCopy.length; i++) {
            const mediaId = get(updPathValueCopy[i], 'id');
            set(differences, path + `[${i}]`, { id: mediaId });
        }
    } else {
        set(differences, path, updPathValueCopy);
    }
};

const handleDiffObject = (path: string, updPathValue: any, attrType: string | undefined, pathStack: string[], updateStack: string[], differences: JsonObject) => {
    // push relation data to updateStack
    if (attrType === 'relation') {
        updateStack.push(path);
    } else if (attrType === 'component') {
        set(differences, path, updPathValue);
        const compKeys = Object.keys(updPathValue);
        for (const key of compKeys) {
            const newPath = path + '.' + key;
            pathStack.push(newPath);
        }
        return;
    } else if (attrType === 'media') {
        const mediaId = get(updPathValue, 'id');
        set(differences, path, { id: mediaId });
        return;
    }
    // set id of obj2
    const id = get(updPathValue, 'id');
    if (id) {
        set(differences, path + '.id', id);
    }

    const attributes = get(updPathValue, 'attributes');
    if (attributes) {
        const attrKeys = Object.keys(attributes);
        for (const key of attrKeys) {
            const newPath = path + '.attributes.' + key;
            pathStack.push(newPath);
        }
    }
};

function getDiffAndRelationPaths(
    app: JsonObject,
    upd: JsonObject,
    schema: any
): [JsonObject, JsonObject, string[]] {
    const differences: JsonObject = {};
    // stack for saving the paths to check
    const pathStack: string[] = [];
    // stack saving relations to update
    const updateStack: string[] = [];

    const appCopy = { data: cloneDeep(app) };
    const updCopy = { data: cloneDeep(upd) };
    // begin with data path
    pathStack.push('data');
    updateStack.push('data');
    const idPath = 'data.id';
    // set id of main record
    set(differences, idPath, get(updCopy, idPath));

    while (pathStack.length > 0) {
        const path = pathStack.pop()!;
        const appPathValue = get(appCopy, path);
        const updPathValue = get(updCopy, path);
        const attrType = getAttributeType(schema, path);
        if (updPathValue || typeof updPathValue === 'boolean') {
            // check if data is identical regardless of order
            if (!isEqual(appPathValue, updPathValue)) {
                //Handle Numbers, Array, Object, primitive type values
                // if (!isNaN(Number(updPathValue))) {
                //   handleNumbers(path, appPathValue, updPathValue, differences);
                // } else
                if (isArray(updPathValue) && updPathValue.length !== 0) {
                    handleDiffArray(appCopy, updCopy, path, appPathValue, updPathValue, attrType, pathStack, differences);
                } else if (
                    isObject(updPathValue) && Object.keys(updPathValue).length !== 0 && attrType !== 'json') {
                    handleDiffObject(path, updPathValue, attrType, pathStack, updateStack, differences);
                    //primitive values or empty array / object
                } else {
                    set(differences, path, updPathValue);
                }
            } else if (attrType === 'media') {
                // get parent type by using getAttribute of parentPath to check component type
                const parentPath = getParentAttributeType(schema, path);
                if (parentPath === 'component') {
                    if (isArray(updPathValue)) {
                        for (let i = 0; i < updPathValue.length; i++) {
                            const mediaId = get(updPathValue[i], 'id');
                            set(differences, path + `[${i}]`, { id: mediaId });
                        }
                    } else {
                        const mediaId = get(updPathValue, 'id');
                        if (mediaId) {
                            set(differences, path, { id: mediaId });
                        }
                    }
                }
            }
        } else {
            set(differences, path, updPathValue);
        }
    }
    return [appCopy, differences, updateStack];
}

const updateRelations = async (
    application: JsonObject,
    differences: JsonObject,
    schema: any,
    relationPaths: string[]
) => {
    const relationPathsCopy = [...relationPaths];
    if (relationPathsCopy[0] === 'data') {
        relationPathsCopy.shift();
    }
    while (relationPathsCopy.length > 0) {
        const relationPath = relationPathsCopy.pop()!;
        const apiName = getAttributeApiName(schema, relationPath);

        const updData = { data: get(differences, relationPath + '.attributes', {}) };
        const appId = get(application, relationPath + '.id');
        const updId = get(differences, relationPath + '.id');
        const updAttr = get(differences, relationPath + '.attributes');

        if (apiName) {
            if (updId) {
                // get parent type by using getAttribute of parentPath to check component type
                const parentType = getParentAttributeType(schema, relationPath);
                const parentIsComponentType = parentType === 'component' ? true : false;
                if (updAttr && updAttr.length !== 0) {
                    await PutRecord(apiName, updId, updData);
                }
                if (isEqual(appId, updId) && !parentIsComponentType) {
                    // clear attribute data
                    if (isElemInArray(differences, relationPath)) {
                        // delete element in array and delete array if it is empty afterwards
                        deleteElemInArray(differences, relationPath);
                    } else {
                        unset(differences, relationPath);
                    }
                } else {
                    // keep id, but clear everything else
                    const idObj = { id: updId };
                    set(differences, relationPath, idObj);
                }
            } else {
                let res = await PostRecord(apiName, updData);
                // const idObj = { id: get(res, 'data.id', null) };
                // logDev("PostRecord: ", apiName, updData, relationPath);
                // set(differences, relationPath, idObj);
                set(differences, relationPath, removeNullDeep(removeDataKeys(get(res, 'data', []))));
            }
        }
    }
};
/**
 * Save the data record in the database. Only the changed data should be saved. The system checks whether the changes are present in the linked data records (relations). If so, only then are they saved. Otherwise not.
 * @param application original record
 * @param updates updates 
 * @param schema 
 * @param api 
 * @param poplevel 
 * @returns 
 */
const saveOnlyChanges = async (
    application: JsonObject,
    updates: JsonObject,
    schema: any,
    _api: string,
    _poplevel: any
) => {
    // logDev('+++++++++++++ saveOnlyChanges');
    // logDev('original application');
    // logDev(application);
    // logDev('updates');
    // logDev(updates);
    // console.log(api, poplevel);
    const [appExpanded, differences, relationPaths]: [
        JsonObject,
        JsonObject,
        string[],
    ] = getDiffAndRelationPaths(application, updates, schema);

    // logDev('++++++++++++ AFTER getDiffAndRelationPaths');
    // logDev('differences');
    // logDev(cloneDeep(differences));
    // logDev('relationPaths');
    // logDev(cloneDeep(relationPaths));
    await updateRelations(appExpanded, differences, schema, relationPaths);
    // logDev('+++++++++++ AFTER update relations #########');
    // logDev('differences');
    // logDev(cloneDeep(differences));
    const updateRecord = cloneDeep(get(differences, 'data'));
    const updId = get(differences, 'data.id');
    const updAttr = get(differences, 'data.attributes');
    if (updId && updAttr) {
        //  const res = await PutRecord(api, updId, { data: updAttr }, poplevel);
        const res = false;
        return [res, updateRecord];
    }
};

export { saveOnlyChanges };








